// ============================================================================
// TAX MASTER SERVICE — app-side adapter for the shared tax-master module
// ============================================================================
// Implements the module's injected TaxMasterApi over FBP's axios client against
// /api/v1/tax-masters — the API template's Tax module, delta-synced into
// Fbp.Api as source (ADR-0047; TaxCore ADR-0022). This adapter and
// TaxMastersHost are the ONLY FBP-specific pieces: components/shared/
// tax-master-module is copied verbatim from EnterpriseTemplates/
// react-project-template (the canonical home since taxcore-web was retired)
// and must stay byte-identical so it can be delta-synced.
//
// Every scaffolded app mounts the identical API, so this adapter is the
// template's unchanged apart from FBP's endpoint-builder helpers. The list is
// server-side paged / searched / filtered / sorted (template 01b5cba); the CSV
// export is gated by UI.tax-masters.EXPORT (seed 135) and the module hides the
// button when the user lacks it.

import { get, post, put, del } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, BackendPagedResult } from '../types';
import type {
  AppendTaxMasterVersionRequest,
  CreateTaxMasterRequest,
  TaxMasterApi,
  TaxMasterPage,
  TaxMasterQuery,
  TaxMasterSummary,
  TaxMasterVersion,
} from 'components/shared/tax-master-module/taxMaster.types';

const BASE = '/tax-masters';
const q = (params: Record<string, unknown>) => buildEndpointWithQuery(buildEndpoint(BASE), params);

const EMPTY_PAGE: TaxMasterPage = {
  items: [], page: 1, pageSize: 25, totalCount: 0, totalPages: 0, hasNext: false, hasPrevious: false,
};

export const taxMasterService: TaxMasterApi = {
  async list(jurisdictionCode: string, query: TaxMasterQuery = {}): Promise<TaxMasterPage> {
    const res = await get<ApiResponse<BackendPagedResult<TaxMasterSummary>>>(
      q({ jurisdictionCode, ...(query as Record<string, unknown>) }),
    );
    const paged = res.data.data;
    if (!paged) return EMPTY_PAGE;
    return {
      items: paged.items ?? [],
      page: paged.page,
      pageSize: paged.pageSize,
      totalCount: paged.totalCount,
      totalPages: paged.totalPages,
      hasNext: paged.hasNext,
      hasPrevious: paged.hasPrevious,
    };
  },

  async get(jurisdictionCode: string, rateCode: string): Promise<TaxMasterVersion[]> {
    const res = await get<ApiResponse<TaxMasterVersion[]>>(
      buildEndpointWithQuery(buildEndpoint(`${BASE}/${rateCode}`), { jurisdictionCode }));
    return res.data.data || [];
  },

  async create(jurisdictionCode: string, request: CreateTaxMasterRequest): Promise<void> {
    await post(q({ jurisdictionCode }), request);
  },

  async appendVersion(
    jurisdictionCode: string, rateCode: string, request: AppendTaxMasterVersionRequest): Promise<void> {
    await post(
      buildEndpointWithQuery(buildEndpoint(`${BASE}/${rateCode}/versions`), { jurisdictionCode }), request);
  },

  async replaceVersion(
    jurisdictionCode: string, rateCode: string, effectiveFrom: string,
    request: AppendTaxMasterVersionRequest): Promise<void> {
    await put(
      buildEndpointWithQuery(
        buildEndpoint(`${BASE}/${rateCode}/versions/${effectiveFrom.slice(0, 10)}`), { jurisdictionCode }),
      request);
  },

  async deleteVersion(
    jurisdictionCode: string, rateCode: string, effectiveFrom: string, reason?: string): Promise<void> {
    await del(
      buildEndpointWithQuery(
        buildEndpoint(`${BASE}/${rateCode}/versions/${effectiveFrom.slice(0, 10)}`),
        { jurisdictionCode, reason }));
  },

  async exportCsv(jurisdictionCode: string, query: TaxMasterQuery = {}): Promise<Blob> {
    const res = await get<Blob>(
      buildEndpointWithQuery(buildEndpoint(`${BASE}/export`), {
        jurisdictionCode, ...(query as Record<string, unknown>), page: undefined, pageSize: undefined,
      }),
      { responseType: 'blob' },
    );
    return res.data;
  },
};

export default taxMasterService;
